import React, { useCallback, useId, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import { SxProps, Theme, lighten, useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import { Checkbox } from '../../Checkbox';
import { Typography } from '../../Typography';
import { TypographyWithOverflowHandler } from '../../TypographyWithOverflowHandler';
/**
 * Design tokens extracted from Figma:
 * - Colors/Text/text-secondary (700): #414651
 * - Colors/Text/text-tertiary (600): #535862
 * - Colors/Text/text-quaternary (500): #717680
 * - Colors/Background/bg-primary: #ffffff
 * - Colors/Background/bg-secondary: #F9FAFB
 * - Colors/Background/bg-disabled: #F2F4F7
 * - Colors/Border/border-primary: #d5d7da
 * - spacing-xs: 4px
 * - spacing-md: 8px
 * - spacing-lg: 12px
 * - spacing-xl: 16px
 * - spacing-xxs: 2px
 * - radius-xl: 12px
 */
const DESIGN_TOKENS = {
  colors: {
    textSecondary: '#414651',
    textTertiary: '#535862',
    textQuaternary: '#717680',
    bgPrimary: '#FFFFFF',
    bgSecondary: '#F9FAFB',
    bgDisabled: '#F2F4F7',
  },
  spacing: {
    xxs: '2px',
    xs: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
  },
  radius: {
    xl: '12px',
  },
} as const;
export type NewCheckboxFilterItemProps = {
  /**
   * The title text to display
   */
  title: string;
  /**
   * Optional subtitle to display below the title
   */
  subtitle?: string;
  /**
   * Whether to show a tooltip with the title text when hovering over the title
   */
  showTitleTooltip?: boolean;
  /**
   * The count to display
   */
  count?: number;
  /**
   * Optional icon element to display on the left side
   */
  icon?: React.ReactNode;
  /**
   * Optional avatar element to display on the left side
   */
  avatar?: React.ReactNode;
  /**
   * Whether the checkbox is checked
   */
  isChecked?: boolean;
  /**
   * Whether the component is disabled
   */
  isDisabled?: boolean;
  /**
   * Callback function when checkbox is clicked
   */
  onChange?: (checked: boolean) => void;
  /**
   * Custom sx styles
   */
  sx?: SxProps<Theme>;
  /**
   * Test ID for the component
   */
  'data-testid'?: string;
};
/**
 * A checkbox filter item component that displays a label, numeric value, optional subtext, and checkbox
 * with support for various visual states (normal, focused, checked, disabled)
 */
export const NewCheckboxFilterItem: React.FC<NewCheckboxFilterItemProps> = ({
  title,
  subtitle,
  showTitleTooltip = false,
  count,
  icon,
  avatar,
  isChecked = false,
  isDisabled = false,
  onChange,
  sx,
  'data-testid': testId,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const checkboxId = useId();
  const checkboxRef = useRef<HTMLButtonElement>(null);
  const hasLeadingGraphic = Boolean(icon || avatar);
  const handleClick = useCallback(() => {
    if (!isDisabled) {
      onChange?.(!isChecked);
    }
  }, [isDisabled, isChecked, onChange]);
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isDisabled && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        onChange?.(!isChecked);
      }
    },
    [isDisabled, isChecked, onChange],
  );
  const getBackgroundColor = () => {
    if (isDisabled) {
      return DESIGN_TOKENS.colors.bgDisabled;
    }
    if (isChecked) {
      return lighten(primaryColor, 0.92);
    }
    if (isFocused) {
      return DESIGN_TOKENS.colors.bgPrimary;
    }
    return DESIGN_TOKENS.colors.bgPrimary;
  };
  const getHoverBackgroundColor = () => {
    if (isDisabled) {
      return DESIGN_TOKENS.colors.bgDisabled;
    }
    if (isChecked) {
      return lighten(primaryColor, 0.88);
    }
    return DESIGN_TOKENS.colors.bgSecondary;
  };
  const getTitleColor = () => {
    if (isDisabled) {
      return DESIGN_TOKENS.colors.textQuaternary;
    }
    if (isChecked) {
      return primaryColor;
    }
    return DESIGN_TOKENS.colors.textSecondary;
  };
  const getSubtitleColor = () => {
    if (isDisabled) {
      return DESIGN_TOKENS.colors.textQuaternary;
    }
    if (isChecked) {
      return primaryColor;
    }
    return DESIGN_TOKENS.colors.textTertiary;
  };
  const getCountColor = () => {
    if (isDisabled) {
      return DESIGN_TOKENS.colors.textQuaternary;
    }
    if (isChecked) {
      return primaryColor;
    }
    return DESIGN_TOKENS.colors.textTertiary;
  };
  return (
    <Box
      data-testid={testId}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onFocus={() => !isDisabled && setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      tabIndex={isDisabled ? -1 : 0}
      aria-disabled={isDisabled}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: DESIGN_TOKENS.spacing.lg,
        paddingX: DESIGN_TOKENS.spacing.xl,
        paddingY: hasLeadingGraphic ? DESIGN_TOKENS.spacing.xxs : DESIGN_TOKENS.spacing.md,
        borderRadius: DESIGN_TOKENS.radius.xl,
        backgroundColor: getBackgroundColor(),
        transition: 'background-color 0.15s ease-in-out, border-color 0.15s ease-in-out',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        minHeight: '36px',
        boxSizing: 'border-box',
        '&:hover': {
          backgroundColor: getHoverBackgroundColor(),
        },
        ...sx,
      }}
    >
      {/* Leading graphic (icon or avatar) */}
      {icon && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            width: '32px',
            height: '32px',
            color: DESIGN_TOKENS.colors.textTertiary,
            '& svg': {
              width: '24px',
              height: '24px',
            },
          }}
        >
          {icon}
        </Box>
      )}
      {avatar && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {avatar}
        </Box>
      )}
      {/* Content (title and subtitle) */}
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
        <Tooltip title={showTitleTooltip ? title : undefined} arrow placement="top">
          <Box sx={{ width: '100%', minWidth: 0 }}>
            <TypographyWithOverflowHandler
              text={title}
              typographyProps={{
                sx: {
                  color: getTitleColor(),
                  fontSize: '14px',
                  fontWeight: 500,
                  lineHeight: '20px',
                },
              }}
            />
          </Box>
        </Tooltip>
        {subtitle && (
          <TypographyWithOverflowHandler
            text={subtitle}
            typographyProps={{
              sx: {
                color: getSubtitleColor(),
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '20px',
                marginTop: DESIGN_TOKENS.spacing.xs,
              },
            }}
          />
        )}
      </Box>

      {/* Count */}
      {count !== undefined && (
        <Typography
          sx={{
            color: getCountColor(),
            fontFamily: 'Inter, sans-serif',
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '20px',
            flexShrink: 0,
          }}
        >
          {count}
        </Typography>
      )}

      {/* Checkbox */}
      <Checkbox
        id={checkboxId}
        ref={checkboxRef}
        size="small"
        checked={isChecked}
        disabled={isDisabled}
        onChange={(e) => {
          e.stopPropagation();
          onChange?.(e.target.checked);
        }}
        sx={{ padding: '4px', flexShrink: 0 }}
      />
    </Box>
  );
};
